import { reactive, ref } from 'vue'
import { githubApi } from '@/api/github'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import { REPO_SCAN_STATUS } from '@/lib/reviewSettingsLabels'
import { pollUntil } from '@/lib/poll'
import type { PaginationMeta, RepoRule, RepoScan } from '@/types/models'

// Singleton, following useGithubRepositorySettings.ts's shape: reactive
// Record keyed by repository-settings id, so rules/scans state survives
// navigating away from and back to a repo's settings page within a session.
const rulesByRepoSettings = reactive<Record<string, RepoRule[]>>({})
const rulesMetaByRepoSettings = reactive<Record<string, PaginationMeta | null>>({})
const scansByRepoSettings = reactive<Record<string, RepoScan[]>>({})
const loading = ref(false)
const scansLoading = ref(false)
const error = ref<string | null>(null)
// Field-keyed 422 messages from the last createRule/updateRule call. The
// rule form reads these to place a message under the input that caused it,
// same pattern as ProjectDetailsSection.vue's store.fieldErrors — `error`
// stays the generic banner.
const fieldErrors = ref<Record<string, string[]>>({})

// Its own ref, not the shared `error` above: importRulesFromSkill can run
// for up to SKILL_IMPORT_POLL_TIMEOUT_MS, far longer than the other actions
// that share `error`. If it used `error`, a failure at the end of that
// window could stomp an unrelated, more recent action's error, or an
// unrelated action could clear this one mid-poll.
const skillImportError = ref<string | null>(null)

// Lets a poll started by a component that has since unmounted stop instead
// of continuing to fire requests and overwrite state for a repo the user
// navigated away from — set(true) from the caller's onBeforeUnmount.
const skillImportPollCancelled = reactive<Record<string, boolean>>({})

const SKILL_IMPORT_POLL_INTERVAL_MS = 3_000
const SKILL_IMPORT_POLL_TIMEOUT_MS = 150_000

// Guards against a stale page's response landing after a newer one's and
// clobbering it — see RepoRulesSection.vue, which fires a new fetchRules
// call per pagination click.
const requestGuard = createRequestGuard()

export function useRepoRules() {
  async function fetchRules(
    installationId: string,
    repositorySettingsId: string,
    page = 1,
    sort?: string,
    title?: string,
  ): Promise<void> {
    if (!repositorySettingsId) return
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load rules.',
      async () => {
        const { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, page, sort, title)
        if (!requestGuard.isCurrent(token)) return
        rulesByRepoSettings[repositorySettingsId] = data
        rulesMetaByRepoSettings[repositorySettingsId] = meta
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  function getRules(repositorySettingsId: string): RepoRule[] {
    return rulesByRepoSettings[repositorySettingsId] ?? []
  }

  function getRulesMeta(repositorySettingsId: string): PaginationMeta | null {
    return rulesMetaByRepoSettings[repositorySettingsId] ?? null
  }

  async function createRule(
    installationId: string,
    repositorySettingsId: string,
    payload: Pick<RepoRule, 'title' | 'description' | 'bad_example' | 'good_example' | 'language'>,
  ): Promise<void> {
    await withAsyncState(
      error,
      'Failed to create rule.',
      async () => {
        await githubApi.createRepoRule(installationId, repositorySettingsId, payload)
        // Rules sort newest-first, so the new rule always lands on page 1 —
        // refetching it keeps both the list and its pagination total accurate,
        // rather than appending locally and letting the total go stale.
        const { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, 1)
        rulesByRepoSettings[repositorySettingsId] = data
        rulesMetaByRepoSettings[repositorySettingsId] = meta
      },
      undefined,
      fieldErrors,
    )
  }

  async function updateRule(
    installationId: string,
    repositorySettingsId: string,
    ruleId: string,
    payload: Partial<Pick<RepoRule, 'title' | 'description' | 'bad_example' | 'good_example' | 'language' | 'status'>>,
  ): Promise<void> {
    await withAsyncState(
      error,
      'Failed to update rule.',
      async () => {
        const updated = await githubApi.updateRepoRule(installationId, repositorySettingsId, ruleId, payload)
        rulesByRepoSettings[repositorySettingsId] = getRules(repositorySettingsId).map((rule) =>
          rule.id === ruleId ? updated : rule,
        )
      },
      undefined,
      fieldErrors,
    )
  }

  async function deleteRule(
    installationId: string,
    repositorySettingsId: string,
    ruleId: string,
    page = 1,
  ): Promise<PaginationMeta | null | undefined> {
    return withAsyncState(error, 'Failed to delete rule.', async () => {
      await githubApi.deleteRepoRule(installationId, repositorySettingsId, ruleId)
      // Refetch the page the rule was deleted from instead of splicing
      // locally, so the pagination total (and remaining pages) stay accurate.
      // Laravel's paginator doesn't clamp, so deleting the last row on a page
      // beyond page 1 would otherwise re-request a now out-of-range page and
      // leave the table empty with no way back — step back a page first, same
      // as projects.ts's deleteProject.
      let { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, page)
      if (data.length === 0 && page > 1) {
        ;({ data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, page - 1))
      }
      rulesByRepoSettings[repositorySettingsId] = data
      rulesMetaByRepoSettings[repositorySettingsId] = meta
      // Report back the page actually landed on, so callers holding their own
      // page ref (e.g. RepoRulesSection.vue's usePagination) can resync it —
      // otherwise a stepped-back page silently diverges from what's displayed.
      return meta
    })
  }

  async function fetchScans(installationId: string, repositorySettingsId: string): Promise<void> {
    if (!repositorySettingsId) return
    await withAsyncState(
      error,
      'Failed to load scan history.',
      async () => {
        scansByRepoSettings[repositorySettingsId] = await githubApi.listRepoScans(installationId, repositorySettingsId)
      },
      scansLoading,
    )
  }

  function getScans(repositorySettingsId: string): RepoScan[] {
    return scansByRepoSettings[repositorySettingsId] ?? []
  }

  function isScanActive(repositorySettingsId: string): boolean {
    return getScans(repositorySettingsId).some(
      (scan) => scan.status === REPO_SCAN_STATUS.QUEUED || scan.status === REPO_SCAN_STATUS.RUNNING,
    )
  }

  async function startScan(installationId: string, repositorySettingsId: string, scope?: string[]): Promise<void> {
    await withAsyncState(error, 'Failed to start scan.', async () => {
      const scan = await githubApi.startRepoScan(installationId, repositorySettingsId, scope)
      scansByRepoSettings[repositorySettingsId] = [scan, ...getScans(repositorySettingsId)]
    })
  }

  async function importRules(installationId: string, repositorySettingsId: string, payload: unknown): Promise<void> {
    await withAsyncState(error, 'Failed to import rules.', async () => {
      await githubApi.importRepoRules(installationId, repositorySettingsId, payload)
      // The endpoint returns only the newly created rows, not the repo's full
      // rule list or its updated pagination meta — rules sort newest-first,
      // so refetching page 1 both surfaces the import and keeps the total
      // count accurate.
      const { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, 1)
      rulesByRepoSettings[repositorySettingsId] = data
      rulesMetaByRepoSettings[repositorySettingsId] = meta
    })
  }

  async function importRulesFromSkill(
    installationId: string,
    repositorySettingsId: string,
    skill: 'otwell' | 'perfecto',
  ): Promise<void> {
    skillImportPollCancelled[repositorySettingsId] = false

    await withAsyncState(skillImportError, 'Failed to import rules.', async () => {
      // The CLI mining runs as a queued job server-side (too slow for a
      // synchronous request) — this just queues it, then polls the rules
      // list until new rows (not present in the baseline) show up, same
      // idea as the "improve rule" page's updated_at polling.
      const baselineIds = new Set(getRules(repositorySettingsId).map((rule) => rule.id))
      await githubApi.importRulesFromSkill(installationId, repositorySettingsId, skill)

      // Rules sort newest-first, so an imported batch always lands on page 1
      // — polling that page is enough to notice it, regardless of which page
      // the table happens to be showing.
      const imported = await pollUntil(
        async () => {
          const { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, 1)
          return data.some((rule) => !baselineIds.has(rule.id)) ? { data, meta } : undefined
        },
        {
          intervalMs: SKILL_IMPORT_POLL_INTERVAL_MS,
          timeoutMs: SKILL_IMPORT_POLL_TIMEOUT_MS,
          isCancelled: () => skillImportPollCancelled[repositorySettingsId],
        },
      )

      if (imported) {
        rulesByRepoSettings[repositorySettingsId] = imported.data
        rulesMetaByRepoSettings[repositorySettingsId] = imported.meta
        return
      }

      // Timed out without seeing new rows — the job may have failed
      // server-side (CLI error, unreadable skill file, unparseable output)
      // or may still be running. Either way, don't silently report success:
      // refresh once so the list reflects whatever it settled on, then throw
      // so withAsyncState surfaces an error instead of leaving the caller
      // thinking the import finished cleanly.
      if (!skillImportPollCancelled[repositorySettingsId]) {
        const { data, meta } = await githubApi.listRepoRules(installationId, repositorySettingsId, 1)
        rulesByRepoSettings[repositorySettingsId] = data
        rulesMetaByRepoSettings[repositorySettingsId] = meta
        throw new Error('Timed out waiting for the import to finish.')
      }
    })
  }

  // Call from the triggering component's onBeforeUnmount so navigating away
  // stops the poll loop instead of letting it keep firing requests for a
  // repo the user is no longer looking at.
  function cancelSkillImportPoll(repositorySettingsId: string): void {
    skillImportPollCancelled[repositorySettingsId] = true
  }

  // Called on logout so the next session doesn't briefly show the previous
  // user's rules/scans before its own fetch resolves, and so a late response
  // from a request the previous session started can't land and repopulate it.
  function reset(): void {
    requestGuard.start()
    for (const key of Object.keys(rulesByRepoSettings)) delete rulesByRepoSettings[key]
    for (const key of Object.keys(rulesMetaByRepoSettings)) delete rulesMetaByRepoSettings[key]
    for (const key of Object.keys(scansByRepoSettings)) delete scansByRepoSettings[key]
    for (const key of Object.keys(skillImportPollCancelled)) skillImportPollCancelled[key] = true
    loading.value = false
    scansLoading.value = false
    error.value = null
    fieldErrors.value = {}
    skillImportError.value = null
  }

  return {
    loading,
    scansLoading,
    error,
    fieldErrors,
    skillImportError,
    fetchRules,
    getRules,
    getRulesMeta,
    createRule,
    updateRule,
    deleteRule,
    fetchScans,
    getScans,
    isScanActive,
    startScan,
    importRules,
    importRulesFromSkill,
    cancelSkillImportPoll,
    reset,
  }
}
